/**
 * Map a `GoLabUploadStatus` to a `<Badge>` descriptor for the
 * Upload History table (UH-FE).
 *
 * Kept separate from `statusBadge.ts` (which covers the 8 dataset-
 * record statuses) so the two helpers can evolve independently —
 * different status sets, different colour palettes, no overlap.
 *
 * Spec §2.3:106 calls for "processed in success colour, anything else
 * in attention colour". This helper interprets that with finer
 * granularity:
 *   - `processed`  → success    (in the gold pool, all good)
 *   - `pending`    → secondary  (neutral — no work in flight)
 *   - `processing` → attention  (COPY in flight; operator interest)
 *   - `failed`     → destructive (alarming — operator action needed)
 *
 * `failed` is upgraded from attention to destructive because a failed
 * upload is more visually critical than an in-flight one — failure
 * needs immediate notice.
 *
 * UH2 partial-rejection downgrade: when `status === 'processed'` AND
 * `recordsRejected > 0`, the badge's variant downgrades from
 * `success` to `attention`. The label stays `"Processed"` — the BE
 * contract still says the upload as a transaction succeeded — but the
 * visual treatment matches the user's mental model that "10 imported,
 * 5 dropped" is not unambiguously a green-light state. The rose
 * subtitle under the records count carries the count itself.
 */
import type { GoLabUploadStatus } from '../../types';
import type { StatusBadgeVariant } from '../../../annotate/components/statusBadge';

export interface UploadStatusBadgeDescriptor {
  variant: StatusBadgeVariant;
  label: string;
}

export const uploadStatusBadge = (
  status: GoLabUploadStatus,
  recordsRejected: number = 0,
): UploadStatusBadgeDescriptor => {
  switch (status) {
    case 'processed':
      // UH2 partial-rejection downgrade — see file-header note.
      if (recordsRejected > 0) {
        return { variant: 'attention', label: 'Processed' };
      }
      return { variant: 'success', label: 'Processed' };
    case 'pending':
      return { variant: 'secondary', label: 'Pending' };
    case 'processing':
      return { variant: 'attention', label: 'Processing' };
    case 'failed':
      return { variant: 'destructive', label: 'Failed' };
  }
};
